<svelte:options tag={null} />

<script lang="ts">
  import Loader from '../loader/Loader.nested.svelte';
  import type {
    ButtonIconMode,
    ButtonSize,
    ButtonTheme,
    ButtonResponsiveSize,
    ButtonResponsiveWidth,
    ButtonType,
    ButtonLoader,
  } from './button.types';

  export let bordered = false;
  export let theme: ButtonTheme | undefined = undefined;
  export let size: ButtonSize = 'm';
  export let full = false;
  export let label: string;
  export let iconname: string | undefined;
  export let iconmode: ButtonIconMode | undefined;
  export let disabled = false;
  export let responsivesize: ButtonResponsiveSize = undefined;
  export let responsivewidth: ButtonResponsiveWidth = undefined;
  export let type: ButtonType = 'button';
  export let loader: string;

  $: loaderOpts = loader ? (JSON.parse(loader) as ButtonLoader) : undefined;
  $: isLoading = loaderOpts?.enabled ?? false;
  $: loaderPosition = loaderOpts?.position ?? 'left';
  $: loaderTheme = loaderOpts?.theme ?? 'primary';
  $: userClass = $$props.class;
  $: attributes = { ...$$restProps };
  $: delete attributes.class;

  function generateIconName(
    size: ButtonSize,
    iconMode: ButtonIconMode,
    iconName: string,
  ): string {
    const isIconOnly = iconMode === 'only';
    let iconSize: string;

    switch (size) {
      case 's':
        iconSize = '24px';
        break;
      case 'm':
        iconSize = isIconOnly ? '32px' : '24px';
        break;
      case 'l':
        iconSize = isIconOnly ? '100%' : '32px';
        break;
    }

    return `${iconName} size="${iconSize}" class="no"`;
  }

  function getAppearanceClass(theme: ButtonTheme, isBordered: boolean): string {
    if (!isBordered && !theme) {
      return '';
    }

    const type = isBordered ? 'bordered' : 'solid';

    if (theme) {
      return `mc-button--${type}-${theme}`;
    }
    return `mc-button--${type}`;
  }

  function getClasses(
    theme: ButtonTheme,
    isBordered: boolean,
    size: ButtonSize,
    isFull: boolean,
  ): string {
    const classes = ['mc-button'];

    const appearanceClass = getAppearanceClass(theme, isBordered);
    if (appearanceClass) {
      classes.push(appearanceClass);
    }

    if (size) {
      classes.push(`mc-button--${size}`);
    }

    if (isFull) {
      classes.push(`mc-button--full`);
    } else {
      classes.push(`mc-button--fit`);
    }

    if (iconmode === 'only') {
      classes.push('mc-button--square');
    }

    if (responsivesize) {
      classes.push(`mc-button--${responsivesize}`);
    }

    if (responsivewidth) {
      classes.push(`mc-button--${responsivewidth}`);
    }

    return classes.join(' ');
  }
</script>

<button
  {type}
  class="{getClasses(theme, bordered, size, full)} {userClass}"
  {disabled}
  {...attributes}
  on:click
>
  {#if iconname && iconmode === 'left'}
    <span class="mc-button__icon">
      {#if isLoading && loaderPosition === 'left'}
        <Loader size="s" theme={loaderTheme} text="TEST" />
      {:else}
        {@html `<${generateIconName(size, iconmode, iconname)} />`}
      {/if}
    </span>
  {/if}

  {#if iconname && iconmode === 'only'}
    <span class="mc-button__icon">
      {#if isLoading}
        <Loader size="m" theme={loaderTheme} text="" />
      {:else}
        {@html `<${generateIconName(size, iconmode, iconname)} size="100%" />`}
      {/if}
    </span>
  {:else}
    {#if isLoading && loaderPosition === 'left' && !iconname}
      <span class="mc-button__icon">
        <Loader size="s" theme={loaderTheme} text="" />
      </span>
    {/if}
    <span class="mc-button__label">{label}</span>
    {#if isLoading && loaderPosition === 'right' && !iconname}
      <span class="mc-button__icon">
        <Loader size="s" theme={loaderTheme} text="" />
      </span>
    {/if}
  {/if}

  {#if iconname && iconmode === 'right'}
    <span class="mc-button__icon">
      {#if isLoading && loaderPosition === 'right'}
        <Loader size="s" theme={loaderTheme} text="" />
      {:else}
        {@html `<${generateIconName(size, iconmode, iconname)} />`}
      {/if}
    </span>
  {/if}
</button>

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';
  @import '@mozaic-ds/styles/components/c.button';

  .mc-button__icon {
    pointer-events: none;
  }
</style>
